/**
 * compatible-bikes-filter.js
 *
 * Reacts to the bike chosen in <motorcycle-selector> on listing pages.
 *
 * Product cards: a card whose `data-compatible-bikes` list contains the
 * selected bike gets a "Fits your bike" tag. Every other card is left alone —
 * no "Doesn't fit", no dimming, no hiding. Supplier fitment data is often
 * incomplete (a part listed "17-21 Touring" may well fit a 2022), so the
 * absence of a match is NOT evidence that a part doesn't fit, and saying so
 * turned riders away from parts that fit their bike.
 *
 * <fitment-bar> (snippets/fitment-bar.liquid): names the rider's bike above
 * the grid and, when the Search & Discovery filter on custom.compatible_bikes
 * is enabled, offers "All parts | Fits my bike" as real server-side filtering
 * — correct counts and pagination across the whole catalogue.
 *
 * State model shared with motorcycle-selector.js / pdp-fitment.js:
 * URL ?bike=, localStorage `forty5_selected_bike` (+ `_label`), and the
 * `forty5:bike-selected` event.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'forty5_selected_bike';
  const LABEL_KEY = 'forty5_selected_bike_label';
  const URL_PARAM = 'bike';
  const SELECTED_EVENT = 'forty5:bike-selected';
  const OPEN_HASH = '#find-my-bike'; /* opens <motorcycle-selector> */
  const FILTER_PARAM = 'filter.p.m.custom.compatible_bikes';
  const CARD_SELECTORS = [
    '.card-wrapper[data-compatible-bikes]',
    '.product-card[data-compatible-bikes]',
    '[data-product-card][data-compatible-bikes]',
  ].join(', ');

  /* ---------------------------------------------------------------- */
  /* Selection                                                         */
  /* ---------------------------------------------------------------- */

  function readSelection() {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get(URL_PARAM);
      if (fromUrl) return fromUrl;
    } catch (_e) {}
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (_e) {
      return null;
    }
  }

  function readLabel() {
    try {
      return localStorage.getItem(LABEL_KEY) || '';
    } catch (_e) {
      return '';
    }
  }

  function openSelector() {
    // motorcycle-selector listens for this hash; clear it first so a second
    // click still fires hashchange.
    if (window.location.hash === OPEN_HASH) window.location.hash = '';
    window.location.hash = OPEN_HASH;
  }

  /* ---------------------------------------------------------------- */
  /* Cards                                                             */
  /* ---------------------------------------------------------------- */

  function bikeListForCard(card) {
    const raw = (card.getAttribute('data-compatible-bikes') || '').trim();
    if (!raw) return [];
    try {
      const list = JSON.parse(raw);
      return Array.isArray(list) ? list : [];
    } catch (_e) {
      return raw.split('|').map((s) => s.trim()).filter(Boolean);
    }
  }

  function applyToCard(card, bikeId) {
    card.classList.remove('forty5-card--fits');
    card.querySelectorAll('.forty5-fitment-badge, .forty5-fitment-sr').forEach((el) => el.remove());
    if (!bikeId || !bikeListForCard(card).includes(bikeId)) return;

    card.classList.add('forty5-card--fits');
    // A <div>, mounted on the card wrapper and absolutely positioned: Dawn's
    // span rules inside .card__information zeroed out an earlier span pill.
    const badge = document.createElement('div');
    badge.className = 'forty5-fitment-badge forty5-fitment-badge--fits';
    badge.setAttribute('aria-hidden', 'true');
    badge.textContent = 'Fits your bike';
    card.appendChild(badge);
    // The badge sits outside the card link, so speak it as part of the link.
    card.querySelectorAll('.card__heading a').forEach((link) => {
      const sr = document.createElement('span');
      sr.className = 'visually-hidden forty5-fitment-sr';
      sr.textContent = ' — fits your bike';
      link.appendChild(sr);
    });
  }

  function applyToCards(bikeId) {
    document.querySelectorAll(CARD_SELECTORS).forEach((c) => applyToCard(c, bikeId));
  }

  /* ---------------------------------------------------------------- */
  /* <fitment-bar>                                                     */
  /* ---------------------------------------------------------------- */

  class FitmentBar extends HTMLElement {
    connectedCallback() {
      this.render();
    }

    _urlFor(bikeId) {
      const url = new URL(window.location.href);
      url.searchParams.delete(this.dataset.filterParam);
      url.searchParams.delete('page');
      url.hash = '';
      if (bikeId) url.searchParams.set(this.dataset.filterParam, bikeId);
      return url.toString();
    }

    render() {
      const bikeId = readSelection();
      const label = readLabel();
      const available = this.hasAttribute('data-filter-available');
      const activeBike = this.dataset.activeBike || '';
      this.replaceChildren();
      this.hidden = false;

      const info = document.createElement('p');
      info.className = 'fitment-bar__info';

      if (!bikeId && !activeBike) {
        info.textContent = 'See which parts fit your bike.';
        this.append(info, this._button('Choose your bike', openSelector));
        return;
      }

      if (bikeId) {
        const tag = document.createElement('span');
        tag.className = 'fitment-bar__label';
        tag.textContent = 'Your bike';
        const name = document.createElement('strong');
        name.className = 'fitment-bar__bike';
        name.textContent = label || 'Selected bike';
        info.append(tag, name, this._button('Change', openSelector, 'link'));
      } else {
        info.textContent = 'Showing parts listed for one bike.';
      }
      this.append(info);

      if (!available) {
        const note = document.createElement('p');
        note.className = 'fitment-bar__note';
        note.textContent = 'Parts confirmed for your bike are tagged “Fits your bike”.';
        this.append(note);
        return;
      }

      // Segmented control as two links, so it needs no JS state and the
      // browser back button behaves.
      const filtering = !!activeBike && (!bikeId || activeBike === bikeId);
      const nav = document.createElement('nav');
      nav.className = 'fitment-bar__segments';
      nav.setAttribute('aria-label', 'Parts shown');
      nav.append(
        this._segment('All parts', this._urlFor(null), !filtering),
        this._segment('Fits my bike', this._urlFor(bikeId || activeBike), filtering)
      );
      this.append(nav);
    }

    _segment(text, href, current) {
      const a = document.createElement('a');
      a.className = 'fitment-bar__segment';
      a.href = href;
      a.textContent = text;
      if (current) a.setAttribute('aria-current', 'true');
      return a;
    }

    _button(text, onClick, variant) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className =
        variant === 'link' ? 'fitment-bar__change link' : 'button button--secondary fitment-bar__choose';
      b.textContent = text;
      b.addEventListener('click', onClick);
      return b;
    }
  }

  if (!customElements.get('fitment-bar')) customElements.define('fitment-bar', FitmentBar);

  /* ---------------------------------------------------------------- */
  /* Category hub + chips (sections/category-hub, snippets/category-chips) */
  /* ---------------------------------------------------------------- */

  // Category links open already filtered to the rider's bike. Shopify ignores
  // the param until the Search & Discovery filter exists, so it's always safe.
  function decorateCategoryLinks(bikeId) {
    document.querySelectorAll('[data-category-link]').forEach((a) => {
      const url = new URL(a.getAttribute('href'), window.location.origin);
      url.searchParams.delete(FILTER_PARAM);
      if (bikeId) url.searchParams.set(FILTER_PARAM, bikeId);
      a.setAttribute('href', url.pathname + url.search);
    });

    document.querySelectorAll('[data-category-hub-bike]').forEach((slot) => {
      slot.replaceChildren();
      const text = document.createElement('span');
      if (bikeId) {
        text.append('Showing parts for your ');
        const name = document.createElement('strong');
        name.textContent = readLabel() || 'bike';
        text.append(name, ' in each category.');
        const change = document.createElement('button');
        change.type = 'button';
        change.className = 'fitment-bar__change link';
        change.textContent = 'Change bike';
        change.addEventListener('click', openSelector);
        slot.append(text, change);
      } else {
        text.textContent = 'Choose your bike first to see only parts that fit it.';
        const choose = document.createElement('button');
        choose.type = 'button';
        choose.className = 'button button--secondary';
        choose.textContent = 'Choose your bike';
        choose.addEventListener('click', openSelector);
        slot.append(text, choose);
      }
    });
  }

  /* ---------------------------------------------------------------- */
  /* Wire up                                                           */
  /* ---------------------------------------------------------------- */

  function refresh() {
    const bikeId = readSelection();
    applyToCards(bikeId);
    decorateCategoryLinks(bikeId);
    document.querySelectorAll('fitment-bar').forEach((bar) => bar.render && bar.render());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  document.addEventListener(SELECTED_EVENT, refresh);
  // Dawn re-renders the grid on facet / pagination changes.
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('forty5:cards-rendered', refresh);
})();
